import {
  COMMAND_CENTER_BASE_URL,
  COUNTRY_CODES,
  SUPPORT_ACTIVITIES,
  TABLES_KEYS
} from 'features/command-center/constants';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { FilterType } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { formatDuration, hasFeatureFlag } from 'UI/utils';
import { OptionRenderers } from 'UI/utils/renderers';

export const PAGE_KEY = TABLES_KEYS.overallPerformance;

const COPIES = strings.commandCenter.overallPerformance;

export const DRAWER_CONTENT_TYPES = {
  NotesForm: 'notesForm',
  FeedbackLog: 'feedbackLog'
};

export const FiltersGroups = [
  {
    id: 'recruiters',
    name: 'Recruiters',
    filters: [
      'employeeNumbers',
      'teams',
      'recruiterStatusesById',
      'healthStatusesById',
      'tenures',
      'channelPartnersEmpNumbers',
      'hireDate',
      'directors',
      'recruitedBy',
      'lastFeedbackActivityTypes',
      'lastFeedbackActivityDate',
      'countryCode'
    ]
  },
  {
    id: 'productivity',
    name: 'Productivity',
    filters: [
      'rangeBySendouts',
      'rangeBySendovers',
      'rangeByPlacements',
      'rangeBySignedAgreements',
      'rangeByActiveSendouts'
    ]
  },
  {
    id: 'financial',
    name: 'Financial',
    filters: [
      'rangeByAvgFeePercentage',
      'rangeByAvgFee',
      'rangeByEstimates',
      'rangeByBillings',
      'rangeByProduction',
      'rangeByCashin',
      'rangeByYearlyProduction',
      'rangeByYearlyCashin',
      'rangeByCareerCashin',
      'rangeByTrackingProduction',
      'rangeByOutstandingBillings',
      'rangeByDrawBalance'
    ],
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceFinancialFilters)
  },
  {
    id: 'metrics',
    name: 'Metrics',
    filters: [
      'rangeByInMails',
      'rangeByBulks',
      'rangeByPeopleReached',
      'rangeByPosts',
      'rangeByNewLivePosts',
      'rangeByPhoneTime',
      'rangeByTotalCalls'
    ],
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceFinancialFilters)
  },
  {
    id: 'inventory',
    name: 'Inventory',
    filters: [
      'rangeByNotSignedCompaniesJobOrders',
      'rangeBySignedCompaniesJobOrders',
      'rangeByCandidates',
      'rangeByContacts',
      'rangeByHiringAuthorities',
      'rangeByCompanies'
    ]
  }
];

const SUPPORT_ACTIVITY_COLUMNS = hasFeatureFlag(
  FeatureFlags.OverallPerformanceFeedbackActivityColumns
)
  ? [
      {
        name: 'lastFeedbackActivityType',
        label: 'Support Activity Type',
        options: {
          sort: true,
          display: true
        }
      },
      {
        name: 'lastFeedbackActivityDate',
        label: 'Support Activity Date',
        options: {
          sort: true,
          display: true,
          renderer: 'date'
        }
      },
      {
        name: 'lastFeedbackActivityGiverName',
        label: 'Support Activity By',
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

const ACTIVE_COLUMNS = hasFeatureFlag(FeatureFlags.OverallPerformanceMetricsFilters)
  ? [
      {
        name: 'activeSendouts',
        label: 'Current Active Sendouts',
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

export const COLUMNS = [
  {
    name: 'idFortpac',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'empNo',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'recruiterName',
    label: 'Name',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewRecruiter,
          idKey: 'empNo',
          textKey: 'name',
          enableInTabRedirect: true
        }
      },
      setCellHeaderProps: () => ({ className: 'dataTableStickyLeftColumnHeader' }),
      setCellProps: () => ({
        className: 'dataTableStickyLeftColumn'
      })
    }
  },
  {
    name: 'team',
    label: 'Team',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewTeam,
          idKey: 'managerEmpNo',
          textKey: 'team',
          enableInTabRedirect: true
        }
      }
    }
  },
  {
    name: 'healthStatus',
    label: 'Current Health Status',
    options: {
      sort: true,
      display: true,
      renderer: 'healthStatusChip'
    }
  },
  {
    name: 'hireDate',
    label: 'Start Date',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'employeeStatus',
    label: 'Recruiter Status',
    options: {
      sort: true,
      display: hasFeatureFlag(FeatureFlags.OverallPerformanceSecondVersion) ? true : 'excluded'
    }
  },
  {
    name: 'directorName',
    label: 'Director',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'teamSupervisorsNames',
    label: 'Support Team',
    options: {
      sort: false,
      display: true,
      setCellHeaderProps: () => ({
        style: { minWidth: 200 }
      })
    }
  },
  {
    name: 'tenure',
    label: 'Tenure',
    options: {
      sort: true,
      display: true,
      renderer: 'tenure'
    }
  },
  {
    name: 'daysInStatus',
    label: 'Days in Status *',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'inMails',
    label: 'InMails',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'inMails',
          goalKey: 'inMailsGoals'
        }
      }
    }
  },
  {
    name: 'avgInMails',
    label: 'Avg Daily InMails',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'avgInMails',
          goalKey: 'avgInMailsGoals'
        }
      }
    }
  },
  {
    name: 'bulks',
    label: 'Bulks',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'bulks',
          goalKey: 'bulksGoal'
        }
      }
    }
  },
  {
    name: 'peopleReached',
    label: 'People Reached',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'peopleReached',
          goalKey: 'peopleReachedGoal'
        }
      }
    }
  },
  {
    name: 'avgTodayLivePosts',
    label: 'Avg Daily Active Posts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'newLivePosts',
    label: 'New Posts',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'calls',
    label: 'Calls',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'calls',
          goalKey: 'callsGoal'
        }
      }
    }
  },
  {
    name: 'callsTime',
    label: 'Phone Time',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'callsTime',
          goalKey: 'callsTimeGoal',
          formatter: formatDuration
        }
      }
    }
  },
  {
    name: 'sendouts',
    label: 'Sendouts',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'sendouts',
          goalKey: 'sendoutsGoal'
        }
      }
    }
  },
  {
    name: 'sendovers',
    label: 'Sendovers',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'placements',
    label: 'Placements',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'feeAgreementsSent',
    label: 'Fee Agreements Sent',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'feeAgreementsSigned',
    label: 'Signed Agreements',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'notSignedCompaniesJo',
    label: 'Not Signed Comp. Job Orders',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'signedCompaniesJo',
    label: 'Signed Comp. Job Orders',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'candidatesAdded',
    label: 'Candidates',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'contactsAdded',
    label: 'Contacts',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'haAdded',
    label: 'Hiring Authorities',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'companiesAdded',
    label: 'Companies',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'estimates',
    label: 'Estimates',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'avgFeePercentage',
    label: 'Avg Fee %',
    options: {
      sort: true,
      display: true,
      renderer: 'percentage'
    }
  },
  {
    name: 'avgFeeMoney',
    label: 'Avg Fee',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'billings',
    label: 'Billings',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'production',
    label: 'Production',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashIn',
    label: 'Cash in',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'outStandingBillings',
    label: 'Current O. Billings',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'drawBalance',
    label: 'Current Draw Balance',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'productionYTD',
    label: 'Production YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashInYTD',
    label: 'Cash in YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'careerCashIn',
    label: 'Career Cash in',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'trackingProduction',
    label: 'Tracking Production',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  ...SUPPORT_ACTIVITY_COLUMNS,
  {
    name: 'recruitedBy',
    label: 'Recruited By',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner1Name',
    label: 'Channel P. 1',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner2Name',
    label: 'Channel P. 2',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'termDate',
    label: 'Term Date',
    options: {
      sort: true,
      display: hasFeatureFlag(FeatureFlags.OverallPerformanceSecondVersion) ? true : 'excluded'
    }
  },
  {
    name: 'location',
    label: 'Location',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'countryCode',
    label: 'Country Code',
    options: {
      sort: true,
      display: true
    }
  },
  ...ACTIVE_COLUMNS,
  {
    name: 'lastFeedbackNoteDate',
    label: 'Last Doc Date',
    options: {
      sort: true,
      display: true,
      renderer: 'date'
    }
  },
  {
    name: 'lastFeedbackNoteGiverName',
    label: 'Last Doc By',
    options: {
      sort: true,
      display: true
    }
  }
];

export const ORDER_BY_OPTIONS = {
  column: 'recruiterName',
  direction: 'asc'
};

export const GOAL_METRICS = [
  'inMailsGoals',
  'callsGoal',
  'bulksGoal',
  'peopleReachedGoal',
  'livePostingsDailyGoal',
  'callsTimeGoal',
  'sendoutsGoal',
  'avgInMailsGoals'
].join(',');

export const METRICS = [
  'avgFeeMoney',
  'avgFeePercentage',
  'avgInMails',
  'avgTodayLivePosts',
  'billings',
  'bulks',
  'calls',
  'callsTime',
  'candidatesAdded',
  'careerCashIn',
  'cashIn',
  'cashInYTD',
  'channelPartner1Name',
  'channelPartner2Name',
  'companiesAdded',
  'contactsAdded',
  'daysInStatus',
  'directorName',
  'drawBalance',
  'estimates',
  'feeAgreementsSent',
  'feeAgreementsSigned',
  'haAdded',
  'healthStatus',
  'hireDate',
  'inMails',
  'newLivePosts',
  'notSignedCompaniesJo',
  'outStandingBillings',
  'peopleReached',
  'placements',
  'posts',
  'production',
  'productionYTD',
  'recruitedBy',
  'recruiterName',
  'sendouts',
  'sendovers',
  'signedCompaniesJo',
  'team',
  'teamSupervisorsNames',
  'tenure',
  'trackingProduction',
  'countryCode',
  'location',
  'lastFeedbackNoteDate',
  'lastFeedbackNoteGiverName'
].join(',');

export const FEEDBACK_ACTIVITIES_METRICS = [
  'lastFeedbackActivityType',
  'lastFeedbackActivityGiverEmpNo',
  'lastFeedbackActivityGiverName',
  'lastFeedbackActivityDate'
].join(',');

export const EMPLOYEE_STATUS_METRICS = ['employeeStatus', 'termDate'];

export const ACTIVE_METRICS = ['activeSendouts'];

export const METRICS_DESCRIPTION = [
  COPIES.footer.metrics.calls,
  COPIES.footer.metrics.phoneTime,
  COPIES.footer.metrics.bulks,
  COPIES.footer.metrics.peopleReached,
  COPIES.footer.metrics.inMails,
  COPIES.footer.metrics.posts
];

export const extraFilters = [
  {
    name: 'recruiterStatusesById',
    paramName: 'employeeStatuses',
    title: 'Recruiter Status',
    type: FilterType.Autocomplete,
    displayKey: 'title',
    url: `${Endpoints.UserStatuses}?excludeFPstatuses=true`,
    disableListWrap: true,
    multiple: true,
    idKey: 'id',
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceSecondVersion)
  },
  {
    name: 'tenures',
    paramName: 'tenures',
    title: 'Tenure',
    type: FilterType.Autocomplete,
    displayKey: 'title',
    api: 'secondary',
    url: `${COMMAND_CENTER_BASE_URL}/catalog/tenures`,
    disableListWrap: true,
    multiple: true,
    idKey: 'id'
  },
  {
    name: 'hireDate',
    type: 'date',
    paramName: 'hireDate',
    title: 'Start Date',
    disableTimeRange: true
  },
  {
    name: 'healthStatusesById',
    paramName: 'statuses',
    title: 'Health Status',
    type: FilterType.Autocomplete,
    displayKey: 'title',
    api: 'secondary',
    url: `${COMMAND_CENTER_BASE_URL}/catalog/health-statuses`,
    disableListWrap: true,
    multiple: true,
    idKey: 'id'
  },
  {
    name: 'rangeBySendouts',
    paramName: 'sendouts',
    title: COPIES.filters.productivity.sendouts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.sendouts
  },
  {
    name: 'rangeBySendovers',
    paramName: 'sendovers',
    title: COPIES.filters.productivity.sendovers,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.sendovers
  },
  {
    name: 'rangeByPlacements',
    paramName: 'placements',
    title: COPIES.filters.productivity.placements,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.placements
  },
  {
    name: 'rangeBySignedAgreements',
    paramName: 'feeAgreementsSigned',
    title: COPIES.filters.productivity.signedAgreements,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.signedAgreements
  },
  {
    name: 'rangeByEstimates',
    paramName: 'estimates',
    title: COPIES.filters.financial.estimates,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.estimates,
    inputType: 'currency'
  },
  {
    name: 'rangeByBillings',
    paramName: 'billings',
    title: COPIES.filters.financial.billings,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.billings,
    inputType: 'currency'
  },
  {
    name: 'rangeByProduction',
    paramName: 'production',
    title: COPIES.filters.financial.production,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.production,
    inputType: 'currency'
  },
  {
    name: 'rangeByCashin',
    paramName: 'cashin',
    title: COPIES.filters.financial.cashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.cashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByYearlyProduction',
    paramName: 'productionYTD',
    title: COPIES.filters.financial.yearlyProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.yearlyProduction,
    inputType: 'currency'
  },
  {
    name: 'rangeByYearlyCashin',
    paramName: 'cashinYTD',
    title: COPIES.filters.financial.yearlyCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.yearlyCashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByCareerCashin',
    paramName: 'careerCashin',
    title: COPIES.filters.financial.careerCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.careerCashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByTrackingProduction',
    paramName: 'trackingProduction',
    title: COPIES.filters.financial.trackingProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.trackingProduction,
    inputType: 'currency'
  },
  {
    name: 'rangeByOutstandingBillings',
    paramName: 'currentOutstandingBillings',
    title: COPIES.filters.financial.outstandingBillings,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.outstandingBillings,
    inputType: 'currency'
  },
  {
    name: 'rangeByDrawBalance',
    paramName: 'currentDrawBalance',
    title: COPIES.filters.financial.drawBalance,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.drawBalance,
    inputType: 'currency'
  },
  {
    name: 'rangeByAvgFeePercentage',
    paramName: 'avgFeePercentage',
    title: COPIES.filters.financial.avgFeePercentage,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFeePercentage,
    inputType: 'percentage'
  },
  {
    name: 'rangeByAvgFee',
    paramName: 'avgFeeMoney',
    title: COPIES.filters.financial.avgFee,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFee,
    inputType: 'currency'
  },
  {
    name: 'rangeByInMails',
    paramName: 'inMails',
    title: COPIES.filters.metrics.inMails,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.inMails
  },
  {
    name: 'rangeByBulks',
    paramName: 'bulks',
    title: COPIES.filters.metrics.bulks,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.bulks
  },
  {
    name: 'rangeByPeopleReached',
    paramName: 'peopleReached',
    title: COPIES.filters.metrics.peopleReached,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.peopleReached
  },
  {
    name: 'rangeByPosts',
    paramName: 'avgTodayLivePosts',
    title: COPIES.filters.metrics.posts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.posts
  },
  {
    name: 'rangeByNewLivePosts',
    paramName: 'newLivePosts',
    title: COPIES.filters.metrics.newLivePosts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.newLivePosts
  },
  {
    name: 'rangeByPhoneTime',
    paramName: 'phoneTime',
    title: COPIES.filters.metrics.phoneTime,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.phoneTime
  },
  {
    name: 'rangeByTotalCalls',
    paramName: 'totalCalls',
    title: COPIES.filters.metrics.totalCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.totalCalls
  },
  {
    name: 'lastFeedbackActivityTypes',
    paramName: 'lastFeedbackActivityTypes',
    title: COPIES.filters.recruiters.lastSupportActivity,
    type: FilterType.Select,
    options: SUPPORT_ACTIVITIES,
    renderOption: OptionRenderers.title,
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceFeedbackActivityColumns)
  },
  {
    name: 'lastFeedbackActivityDate',
    paramName: 'lastFeedbackActivityDate',
    title: COPIES.filters.recruiters.lastSupportActivityDate,
    type: 'date',
    disableTimeRange: true,
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceFeedbackActivityColumns)
  },
  {
    name: 'recruitedBy',
    paramName: 'recruitedBy',
    title: 'Recruited By',
    type: FilterType.Input
  },
  {
    name: 'channelPartnersEmpNumbers',
    paramName: 'channelPartners',
    title: COPIES.filters.recruiters.channelPartner,
    type: FilterType.Autocomplete,
    displayKey: 'full_name',
    url: `${Endpoints.Users}?extraColumns=external_employee_id`,
    multiple: true,
    idKey: 'external_employee_id'
  },
  {
    name: 'rangeByNotSignedCompaniesJobOrders',
    paramName: 'notSignedCompaniesJo',
    title: COPIES.filters.inventory.notSignedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.notSignedCompaniesJobOrders
  },
  {
    name: 'rangeBySignedCompaniesJobOrders',
    paramName: 'signedCompaniesJo',
    title: COPIES.filters.inventory.signedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.signedCompaniesJobOrders
  },
  {
    name: 'rangeByCandidates',
    paramName: 'candidatesAdded',
    title: COPIES.filters.inventory.candidates,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.candidates
  },
  {
    name: 'rangeByContacts',
    paramName: 'contactsAdded',
    title: COPIES.filters.inventory.contacts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.contacts
  },
  {
    name: 'rangeByHiringAuthorities',
    paramName: 'haAdded',
    title: COPIES.filters.inventory.hiringAuthorities,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.hiringAuthorities
  },
  {
    name: 'rangeByCompanies',
    paramName: 'companiesAdded',
    title: COPIES.filters.inventory.companies,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.companies
  },
  {
    name: 'countryCode',
    paramName: 'countryCode',
    title: COPIES.filters.recruiters.country,
    type: FilterType.Select,
    options: COUNTRY_CODES,
    renderOption: OptionRenderers.title
  },
  {
    name: 'rangeByActiveSendouts',
    paramName: 'activeSendouts',
    title: COPIES.filters.productivity.activeSendouts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.productivity.activeSendouts,
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceMetricsFilters)
  }
];

export const TENURE_RANGES = {
  6: '0 - 6',
  12: '7 - 12',
  24: '13 - 24',
  25: '25+'
};
